import type { NavSectionLink } from "src/components/layout/nav/nav-section";
import { useContextHubSection } from "src/components/layout/nav/sections/context-hub";
import { useShowAgentsInNav } from "src/components/layout/nav/sections/hooks/agents";
import { useLastSettingsPath } from "src/components/layout/nav/sections/hooks/last-settings-path";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useSpaceContext } from "src/components/spaces";
import { useTemplateLibraryAccess } from "src/components/template-library/use-template-library-access";
import { useAgentsEnabledFlag } from "src/pages/agents/hooks/use-agents-enabled-flag";
import { useKnowledgeLinks } from "src/pages/context-hub/components/knowledge-nav-items";
import { KNOWLEDGE_BASE } from "src/pages/context-hub/components/use-knowledge-section";

/** The custom agents, which Context lists after Context Hub and the templates. */
export const CUSTOM_AGENTS_PATH = "/agents/custom-agents";

type ContextLink = Pick<NavSectionLink, "href" | "label" | "icon"> & {
  /** Another page that lights the entry. */
  alsoActiveOn?: string;
};

/** Context's sidebar, under each page's gates: Context Hub's pages with Knowledge's listed in its place, the template library, then the custom agents. */
export const useContextLinks = (): {
  context: ContextLink[];
  templates: ContextLink | null;
  agents: ContextLink[];
} => {
  const contextHub = useContextHubSection();
  const knowledgeLinks = useKnowledgeLinks();
  const templateLibrary = useTemplateLibraryAccess();
  const { isPermitted: canConfigureAgents } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_configure_agents" },
  });
  const showAgents = useShowAgentsInNav();
  const isAgentsEnabled = useAgentsEnabledFlag();
  const { isInSpace, isPathOrChildrenAccessibleInSpace } = useSpaceContext();
  const isReachable = (href: string) =>
    !isInSpace || isPathOrChildrenAccessibleInSpace(href);

  const context =
    contextHub.isEnabled && canConfigureAgents
      ? contextHub.links.flatMap((link) =>
          !link || !isReachable(link.href)
            ? []
            : // Knowledge's pages stand in for it, so it needs no rail of its own.
              link.href === KNOWLEDGE_BASE
              ? knowledgeLinks
              : [{ href: link.href, label: link.label }],
        )
      : [];
  const [templatesPath, otherTemplatesPath] = templateLibrary.paths;

  const agents =
    showAgents && isAgentsEnabled && isReachable(CUSTOM_AGENTS_PATH)
      ? [{ href: CUSTOM_AGENTS_PATH, label: "Custom agents" }]
      : [];

  return {
    context,
    // One entry for the library, which opens on its first tab and stays lit on the other.
    templates: templatesPath
      ? {
          href: templatesPath,
          label: "Templates",
          alsoActiveOn: otherTemplatesPath,
        }
      : null,
    agents,
  };
};

/** Where the nav's Context opens: back on the page last open under it while it still lists it, otherwise its first page; null when it lists none. */
export const useContextHomePath = (): string | null => {
  const { context, templates, agents } = useContextLinks();
  const lastPath = useLastSettingsPath("context");
  const paths = [
    ...context.map((link) => link.href),
    ...(templates ? [templates.href] : []),
    ...agents.map((link) => link.href),
  ];
  const listedPaths = [
    ...paths,
    ...(templates?.alsoActiveOn ? [templates.alsoActiveOn] : []),
  ];

  const lastPathname = lastPath?.split(/[?#]/)[0];
  const isLastPathAvailable =
    lastPathname !== undefined &&
    listedPaths.some(
      (path) => lastPathname === path || lastPathname.startsWith(`${path}/`),
    );
  if (lastPath && isLastPathAvailable) return lastPath;

  return paths[0] ?? null;
};
